import { Injectable, inject } from '@angular/core';
import { CompanyService } from '../../core/services/company.service';
import { ToastService } from '../../core/services/toast.service';
import { DialogService } from './dialog.service';
import { confirmWord, plural } from '../../core/utils/format.util';
import { CompanyStatus } from '../../core/models/company.model';

export interface IDeletableCompany {
  idCompany: string;
  name: string;
  publishedCards?: number;
  clients?: number;
  frontUrl?: string | null;
}

/**
 * Acciones de negocio compartidas: diálogo 017 «Eliminar negocio» (desde Negocios y desde Zona de peligro) y
 * «Activar / Desactivar negocio» (solo sudo: banner del negocio, Zona de peligro y Todos los negocios).
 */
@Injectable({ providedIn: 'root' })
export class CompanyActionsService {
  private companies = inject(CompanyService);
  private dialog = inject(DialogService);
  private toast = inject(ToastService);

  // region confirmDelete
  async confirmDelete(c: IDeletableCompany): Promise<boolean> {
    // Borrado real: la console-api elimina el negocio con todas sus tarjetas, sellos, clientes y miembros.
    const word = confirmWord(c.name);
    const impact = [
      'Se borran todas sus tarjetas, publicadas y en borrador',
      'Se borran todos sus sellos',
      c.clients != null
        ? `${plural(c.clients, 'cliente perderá', 'clientes perderán')} su avance y sus recompensas`
        : 'Sus clientes perderán su avance y sus recompensas',
      'Sus miembros perderán el acceso',
    ];
    return this.dialog.confirm({
      icon: 'trash',
      media: c.frontUrl ? { kind: 'card', url: c.frontUrl } : undefined,
      title: `¿Eliminar ${c.name}?`,
      body: 'Se borrará definitivamente, junto con todas sus tarjetas, sellos, clientes y miembros. No se puede deshacer.',
      impact,
      confirmWord: word,
      confirmLabel: 'Eliminar negocio',
      action: async () => {
        const res = await this.companies.delete(c.idCompany, word);
        if (!res.ok) { this.toast.error(res.message || 'No pudimos eliminar el negocio'); return false; }
        this.toast.success('Negocio eliminado');
        if (this.companies.current()?.idCompany === c.idCompany) this.companies.clear();
        this.companies.mine.update(list => list.filter(x => x.idCompany !== c.idCompany));
        return true;
      },
    });
  }

  // region confirmStatus
  /** Solo sudo: confirma y activa (1) o desactiva (0) el negocio. Toast con el `message` de la API. */
  async confirmStatus(c: { idCompany: string; name: string }, status: CompanyStatus): Promise<boolean> {
    const activate = status === 1;
    return this.dialog.confirm({
      icon: activate ? 'check' : 'alert',
      iconTone: activate ? 'brand' : 'danger',
      tone: activate ? 'primary' : 'danger',
      size: 'm',
      title: activate ? `¿Activar ${c.name}?` : `¿Desactivar ${c.name}?`,
      body: activate
        ? 'Su dueño, administradores y staff podrán usarlo y editarlo.'
        : 'Su dueño, administradores y staff no podrán usarlo ni modificar nada hasta que lo vuelvas a activar.',
      confirmLabel: activate ? 'Activar negocio' : 'Desactivar negocio',
      action: async () => {
        const res = await this.companies.setStatus(c.idCompany, status);
        if (!res.ok) {
          this.toast.error(res.message || (activate ? 'No pudimos activar el negocio' : 'No pudimos desactivar el negocio'));
          return false;
        }
        this.toast.success(res.message || (activate ? 'Negocio activado' : 'Negocio desactivado'));
        return true;
      },
    });
  }
}
